export interface StartLevelInput {
  width?: number;
  height?: number;
}

export interface IndexedStartLevelInput extends StartLevelInput {
  originalIndex?: number;
}

/**
 * Pick a start level that lands at the resolution the player will end up at,
 * to avoid the visible ABR climb from the bottom of the ladder.
 *
 * Two policies, depending on whether `level-cap` is active:
 *
 * - `levelCap=true`: mirror hls.js `capLevelToPlayerSize`
 *   (`CapLevelController.getMaxLevelByMediaSize`), which caps at the smallest
 *   rung whose width OR height reaches the larger player dimension (in CSS
 *   px, because the cap is configured with `ignoreDevicePixelRatio=true`).
 *   We start at that same rung so the start level never exceeds the cap: a
 *   start above the cap is not honoured by hls.js, which then begins on the
 *   cap rung itself, the last codec of that size group, and the session
 *   inherits that codec instead of the selected one. Falls back to the
 *   highest available rung when every rung is smaller than the player (cap
 *   doesn't restrict in that case).
 *
 * - `levelCap=false`: there is no upper bound, but downloading a rung above
 *   what the display can show is wasted bandwidth, so pick the highest rung
 *   that fits within the DPR-adjusted player size. Returns -1 when no rung
 *   fits (player smaller than the smallest rung), letting hls.js's default
 *   ABR pick the start level.
 *
 * Returns -1 when the player size is unknown (not yet laid out), so the
 * caller can fall back to the default ABR start level.
 */
export function pickStartLevelForPlayerSize(
  levels: StartLevelInput[],
  playerWidth: number,
  playerHeight: number,
  levelCap: boolean,
  devicePixelRatio: number,
): number {
  if (levels.length === 0) return -1;
  if (playerWidth < 1 || playerHeight < 1) return -1;

  const dpr = levelCap ? 1 : devicePixelRatio;
  const targetWidth = Math.round(playerWidth * dpr);
  const targetHeight = Math.round(playerHeight * dpr);

  if (levelCap) {
    // hls.js compares both rung dimensions against the larger player
    // dimension so that aspect-ratio changes do not toggle the cap.
    const squareSize = Math.max(targetWidth, targetHeight);
    let capIndex = -1;
    let capHeight = Infinity;
    let highestIndex = -1;
    let highestHeight = 0;

    for (let i = 0; i < levels.length; i++) {
      const w = levels[i].width ?? 0;
      const h = levels[i].height ?? 0;
      if (w === 0 && h === 0) continue;

      if (h > highestHeight) {
        highestHeight = h;
        highestIndex = i;
      }

      if ((w >= squareSize || h >= squareSize) && h < capHeight) {
        capHeight = h;
        capIndex = i;
      }
    }

    return capIndex >= 0 ? capIndex : highestIndex;
  }

  let bestIndex = -1;
  let bestHeight = 0;
  for (let i = 0; i < levels.length; i++) {
    const w = levels[i].width ?? 0;
    const h = levels[i].height ?? 0;
    if (w === 0 && h === 0) continue;

    if (
      (w === 0 || w <= targetWidth) &&
      (h === 0 || h <= targetHeight) &&
      h > bestHeight
    ) {
      bestHeight = h;
      bestIndex = i;
    }
  }
  return bestIndex;
}

/**
 * Pick from a filtered rendition list while returning the corresponding index
 * in hls.js's unfiltered level list.
 */
export function pickOriginalStartLevelForPlayerSize(
  levels: IndexedStartLevelInput[],
  playerWidth: number,
  playerHeight: number,
  levelCap: boolean,
  devicePixelRatio: number,
): number {
  const filteredIndex = pickStartLevelForPlayerSize(
    levels,
    playerWidth,
    playerHeight,
    levelCap,
    devicePixelRatio,
  );
  if (filteredIndex < 0) return -1;
  return levels[filteredIndex].originalIndex ?? filteredIndex;
}

/**
 * hls.js's player-size cap (CapLevelController.getMaxLevelByMediaSize) over
 * the full, height-sorted level list: the last entry of the first size group
 * whose width or height reaches the larger player dimension (CSS px), the
 * top level when no rung is that large, -1 without a player size. A start
 * level above this index is not honoured by hls.js, which begins on the cap
 * rung itself instead.
 */
export function getPlayerSizeCapIndex(
  levels: StartLevelInput[],
  playerWidth: number,
  playerHeight: number,
): number {
  if (levels.length === 0 || playerWidth < 1 || playerHeight < 1) return -1;
  const squareSize = Math.max(playerWidth, playerHeight);
  for (let i = 0; i < levels.length; i++) {
    const level = levels[i];
    const next = levels[i + 1];
    const lastOfSize = !next || next.width !== level.width ||
      next.height !== level.height;
    if (
      ((level.width ?? 0) >= squareSize || (level.height ?? 0) >= squareSize) &&
      lastOfSize
    ) {
      return i;
    }
  }
  return levels.length - 1;
}
